import moment from "moment";
import { useCallback, useState } from "react";
import { DateRangeCalendar, TextInput } from "risa-oasis-ui_v2";

export interface AsteraTrackStatusDateRange {
  startDate: Date | null;
  endDate: Date | null;
}

interface AsteraTrackStatusContentProps {
  /** When true, renders Apply and Cancel buttons (e.g. when embedded in a tab) */
  showActions?: boolean;
  onApply?: (dateRange: AsteraTrackStatusDateRange) => void;
  onCancel?: () => void;
  /** Called when date range selection changes (e.g. for parent to control save button disabled state). When hasSelection is true, dateRange is provided. */
  onDateRangeChange?: (
    hasSelection: boolean,
    dateRange?: AsteraTrackStatusDateRange,
  ) => void;
}

const AsteraTrackStatusContent = (props: AsteraTrackStatusContentProps) => {
  const { onDateRangeChange } = props;

  const [dateRange, setDateRange] = useState<AsteraTrackStatusDateRange>({
    startDate: null,
    endDate: null,
  });
  const [calendarKey, setCalendarKey] = useState(0);

  const handleDateRangeChange = useCallback(
    (startDate: Date | null, endDate: Date | null) => {
      const next = { startDate, endDate };
      setDateRange(next);
      const hasSelection = startDate !== null && endDate !== null;
      onDateRangeChange?.(hasSelection, hasSelection ? next : undefined);
    },
    [onDateRangeChange],
  );

  const handleClearSelection = useCallback(() => {
    setDateRange({ startDate: null, endDate: null });
    setCalendarKey((k) => k + 1);
    onDateRangeChange?.(false, undefined);
  }, [onDateRangeChange]);

  return (
    <div className="astera-track-status__container flex flex-col">
      <div className="astera-track-status__date-fields flex justify-center gap-3">
        <TextInput
          key={`from-${dateRange.startDate?.toISOString() ?? "empty"}`}
          id="track-status-from"
          label="From"
          readOnly
          defaultValue={
            dateRange.startDate
              ? moment(dateRange.startDate).format("DD MMM YYYY")
              : ""
          }
        />

        <TextInput
          key={`to-${dateRange.endDate?.toISOString() ?? "empty"}`}
          id="track-status-to"
          label="To"
          readOnly
          defaultValue={
            dateRange.endDate
              ? moment(dateRange.endDate).format("DD MMM YYYY")
              : ""
          }
        />
      </div>

      <div className="astera-track-status__calendar mt-1 flex justify-center">
        <DateRangeCalendar
          key={calendarKey}
          onDateRangeChange={(start: Date | null, end: Date | null) => {
            handleDateRangeChange(start, end);
          }}
        />
      </div>

      <div className="astera-track-status__actions flex justify-end">
        <div
          onClick={handleClearSelection}
          className="justify-end text-small font-semibold text-red-500 hover:cursor-pointer hover:shadow-sm"
        >
          Clear Selection
        </div>
      </div>
    </div>
  );
};

export default AsteraTrackStatusContent;
